import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

export interface IVisionariesCategory {
  id?: number;
  visionaries_id?: number;
  name: string;
  text_color?: string;
  border_color?: string;
  background_color?: string;
  active_text_color?: string;
  active_background_color?: string;
  active_border_color?: string;
  order_index: number;
  is_active?: boolean;
  is_default?: boolean;
  /** Hybrid CMS: pool profile ids to show under this category (omit when is_default). */
  profile_ids?: number[];
  profiles?: IVisionariesProfile[];
}

export interface IVisionariesProfile {
  id?: number;
  category_id?: number | null;
  priority: number;
  desktop_image?: string;
  mobile_image?: string;
  image_alt?: string;
  profile_name: string;
  profile_title?: string;
  profile_name_color?: string;
  profile_title_color?: string;
  card_overlay_gradient_start?: string;
  card_overlay_gradient_end?: string;
  card_overlay_gradient_angle?: string;
  logo_1?: string;
  logo_1_alt?: string;
  logo_2?: string;
  logo_2_alt?: string;
  logos_layout?: string;
  is_active?: boolean;
}

export interface IVisionaries {
  id?: number;
  page_id?: number;
  component_id?: number;
  variant?: string;
  status?: boolean;
  use_categories?: boolean;
  enable_animation?: boolean | number;
  enable_coverflow_carousel?: boolean | number;
  categories?: IVisionariesCategory[];
  pool_profiles?: IVisionariesProfile[];
}

function isCreateSuccessStatus(status: number | undefined) {
  return status === 201 || status === 200;
}

export async function createVisionariesCategory(payload: Partial<IVisionariesCategory>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.VISIONARIES.CATEGORY.CREATE,
      payload
    );
    return isCreateSuccessStatus(response?.status) ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateVisionariesCategory(id: number, payload: Partial<IVisionariesCategory>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.VISIONARIES.CATEGORY.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteVisionariesCategory(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.VISIONARIES.CATEGORY.DELETE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getVisionariesCategoriesByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.VISIONARIES.CATEGORY.GET_BY_COMPONENT_ID(componentId)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function createVisionaries(payload: Partial<IVisionaries>) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.VISIONARIES.CREATE, payload);
    return isCreateSuccessStatus(response?.status) ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateVisionaries(id: number, payload: Partial<IVisionaries>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.VISIONARIES.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getVisionariesById(id: number) {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.VISIONARIES.GET_BY_ID(id));
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getVisionariesByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.VISIONARIES.GET_BY_COMPONENT_ID(componentId)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteVisionaries(id: number) {
  try {
    const response = await adminAxios.delete(ADMIN_API_ENDPOINTS.VISIONARIES.DELETE(id));
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function createVisionariesProfile(payload: Partial<IVisionariesProfile>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.VISIONARIES.PROFILE.CREATE,
      payload
    );
    return isCreateSuccessStatus(response?.status) ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateVisionariesProfile(id: number, payload: Partial<IVisionariesProfile>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.VISIONARIES.PROFILE.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getVisionariesProfilesByCategoryId(categoryId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.VISIONARIES.PROFILE.GET_BY_CATEGORY_ID(categoryId)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteVisionariesProfile(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.VISIONARIES.PROFILE.DELETE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function cloneVisionariesWithCategories(
  sourceVisionariesId: number,
  targetPageComponentId: number,
  targetVisionariesId?: number | null
) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.VISIONARIES.CLONE, {
      source_visionaries_id: sourceVisionariesId,
      target_page_component_id: targetPageComponentId,
      target_visionaries_id: targetVisionariesId ?? null,
    });
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}
